import React, {useState, useEffect} from 'react';
import styled from 'styled-components';
import API, {graphqlOperation} from '@aws-amplify/api';
import {parse, differenceInDays, format, parseISO} from 'date-fns';
import {ToastContainer, toast, Slide} from 'react-toastify';
import {IntercomAPI} from 'react-intercom';
import Storage from '@aws-amplify/storage';

import {useUserDataContext} from '@bootcamp/web/src/contexts/UserData';
import {useModalContext} from '@bootcamp/web/src/contexts/Modal';
import {distributeEvents, calculateDaysOff} from './helpers';
import {UpdateComplete} from './components/shared';

import {H1, Body4} from '@bootcamp/web/src/components/Typography/next';
import {useDocumentTitle} from '@bootcamp/shared/src/util/hooks';
import Container from '../PageContainer';
import Breadcrumbs from '../../../components/NavBar/components/Breadcrumbs';

import Create from './components/Create';
import SettingsModal from './components/Modals/Settings';
import RebalanceModal, {UndoRebalanceConfirmation} from './components/Modals/Rebalance';
import CalendarComponent from './components/Calendar';

import {TargetArrow} from '@styled-icons/fluentui-system-filled/TargetArrow';
import {Dismiss} from '@styled-icons/fluentui-system-filled/Dismiss';

import 'react-toastify/dist/ReactToastify.css';
import {Info} from '@styled-icons/fluentui-system-filled/Info';
import {RoundButton} from '@bootcamp/web/src/components/UI';
import {Wand} from '@styled-icons/fluentui-system-filled/Wand';
import {Settings} from '@styled-icons/fluentui-system-filled/Settings';
import {TooltipProvider} from './contexts/Tooltip';

const StyledToastContainer = styled(ToastContainer)`
  --toastify-toast-width: auto;

  .Toastify__toast {
    border-radius: 8px;
    background: transparent;
    ${({theme}) => theme.mediaQueries.custom(480)} {
      width: calc(100vw - 32px) !important;
      left: 0 !important;
      right: 0 !important;
      margin: 0 auto !important;
      bottom: 16px !important;
    }
  }
  .Toastify__toast-container {
    padding: 0px;
    position: absolute;
  }
  .Toastify__toast-body {
    padding: 0;
  }
`;
const StyledBreadcrumbs = styled(Breadcrumbs)`
  margin-bottom: ${({theme}) => theme.layouts.spacing.s};

  ${({theme}) => theme.mediaQueries.tablet} {
    * {
      font-size: 16px !important;
      line-height: 16px !important;
    }
    margin-bottom: 8px;
  }
`;
const PageTitle = styled(H1)`
  color: white;
  margin-top: 0px;
  margin-bottom: ${({theme}) => theme.layouts.spacing.m};

  ${({theme}) => theme.mediaQueries.tablet} {
    margin-bottom: ${({theme}) => theme.layouts.spacing.s};
    font-size: 28px;
    line-height: 35px;
  }
`;

const ControlContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({theme}) => theme.layouts.spacing.m};
`;

const PillContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
`;

const Pill = styled.div`
  padding: 4px 8px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: ${({theme}) => theme.colors.neutralsPalette.white};
  font-size: 14px;
  line-height: 100%;
  font-weight: 400;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: proxima-nova, sans-serif;
`;
const ProgressBarContainer = styled.div`
  width: 65px;
  height: 8px;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.04) 0%, rgba(0, 0, 0, 0.02) 100%);
  box-shadow: 0px 4px 8px 0px #0000000F inset;
  position: relative;
  border-radius: 8px;
`;
const ProgressBar = styled.div`
  width: ${({progress}) => progress}%;
  height: 100%;
  background: ${({theme}) => theme.colors.brandPalette.blue.gradient};
  border-radius: 8px;
  position: absolute;
  top: 0;
  left: 0;
`;

const BlurbContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 16px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  gap: 8px;
  margin-bottom: 12px;
`;

const InfoIcon = styled(Info)`
  color: #FFFFFFB2;
`;

const InfoIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 100px;
  margin-right: 0px;
`;

const BlurbText = styled(Body4)`
  color: white;
  margin-left: 0px;
  margin-right: auto;
  flex: 1;

  ${({theme}) => theme.mediaQueries.mobileL} {
    display: none;
  }
`;
const BlurbTextMobile = styled(BlurbText)`
  display: none;

  ${({theme}) => theme.mediaQueries.mobileL} {
    display: flex;
  }
`;

const DismissIcon = styled(Dismiss)`
  color: white;
`;
const DismissIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 100px;
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;


const Blurb = ({text, mobileText}) => {
  const [open, setOpen] = useState(true);

  if (!open) return null;

  return (
    <BlurbContainer>
      <InfoIconContainer children={<InfoIcon size={14} />} />
      <BlurbText children={text} />
      <BlurbTextMobile children={mobileText} />
      <DismissIconContainer onClick={() => setOpen(false)} children={<DismissIcon size={16} />} />
    </BlurbContainer>
  )
}

const StyledButton = styled(RoundButton)`
  box-shadow: none;
  background: rgba(255, 255, 255, 0.1);
  div {
    font-weight: 700;
  }
  color: white;
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const StudySchedule = ({match, history}) => {
  const {DEFAULT_USER_ID, bootcamp, saveUserInteraction, searchUserInteractions, isBootcampPlusStudent} = useUserDataContext();
  useDocumentTitle(`${bootcamp == 'med-school' ? 'Med School' : bootcamp.toUpperCase()} Bootcamp | Study Schedule`);

  const [loading, setLoading] = useState(true);
  const [value, setValue] = useState(null);
  const [content, setContent] = useState([]);
  const {modalDispatch} = useModalContext();

  const examDate = searchUserInteractions(`testDate`, false);
  const studySpeed = searchUserInteractions(`studySpeed`, 1);
  
  const incompleteEvents = value
    ? Object.values(value).reduce((acc, events) => acc.concat(events.filter(event => !event.complete)), [])
    : [];
  const remainingHours = Math.ceil(incompleteEvents.reduce((acc, event) => acc + (parseInt(event.estimatedTime || 0) * (event?.custom ? 1 : studySpeed)), 0) / 60);
  const totalEvents = value
    ? Object.values(value).reduce((acc, events) => acc + events.length, 0)
    : 0;
  const completedEvents = value
    ? Object.values(value).reduce((acc, events) => acc + events.filter(event => event.complete).length, 0)
    : 0;
  const daysUntilExam = examDate
    ? differenceInDays(parse(examDate, 'yyyy-MM-dd', new Date()), new Date()) + 1
    : 0;
  const overdueIncompleteEvents = value
    ? Object.entries(value).reduce((acc, [date, events]) => {
        const today = new Date().toISOString().slice(0, 10);
        const isPast = date < today;
        if (isPast) {
          acc = acc.concat(events.filter(event => !event.complete));
        }
        return acc;
      }, [])
    : [];

  const daysOff = calculateDaysOff(value);

  const resetSchedule = async () => {
    await API.graphql({query:`
      mutation deleteQuickCache($input: DeleteQuickCacheInput!) {
        deleteQuickCache(input: $input) {
          id
        }
      }
    `, variables: {input: {id: `${DEFAULT_USER_ID}-${bootcamp}-study-schedule`}}, authMode: 'AMAZON_COGNITO_USER_POOLS'});

    setValue(null);
    setLoading(true);
    init();
  };

  const rebalanceSchedule = async (startDate, daysOff=[], newExamDate, hoursPerDay) => {
    setLoading(true);

    // take current events, and redistribute all incomplete events according to daysOff and hoursPerDay
    const completedEvents = value ? Object.entries(value).reduce((acc, [date, events]) => {
      const filteredEvents = events.filter(event => event.complete);
      return filteredEvents?.length ? {...acc, [date]: filteredEvents} : acc;
    }, {}) : {};
    const distributedEvents = distributeEvents(incompleteEvents, startDate, daysOff, hoursPerDay, studySpeed);
    // merge completed events with distributedEvents

    // distributedEvents is an object with date keys and event arrays as values
    // completedEvents is an object with date keys and event arrays as values
    // merge the distributedEvents and completedEvents objects
    const distributedEventsFlat = Object.entries(distributedEvents)
    const completedEventsFlat = Object.entries(completedEvents)
      
    const mergedEvents = [...distributedEventsFlat, ...completedEventsFlat].reduce((acc, [date, events]) => {
      if (acc[date]) {
        acc[date] = [...acc[date], ...events];
      } else {
        acc[date] = events;
      }
      return acc;
    }, {});

    // update quickCache
    await API.graphql({query:`
      mutation updateQuickCache($input: UpdateQuickCacheInput!) {
        updateQuickCache(input: $input) {
          id
        }
      }
    `, variables: {input: {id: `${DEFAULT_USER_ID}-${bootcamp}-study-schedule`, content: JSON.stringify(mergedEvents)}}, authMode: 'AMAZON_COGNITO_USER_POOLS'});

    await IntercomAPI('trackEvent', 'Rebalanced Study Schedule', {startDate, oldExamDate: examDate, newExamDate, daysOff: daysOff?.length, hoursPerDay: Math.ceil(hoursPerDay)});
    await saveUserInteraction(`testDate`, JSON.stringify(newExamDate));

    setValue(mergedEvents);
    setLoading(false);

    toast(<UpdateComplete iconName="wand" title="Rebalance Successful" body={<>You'll be studying about {Math.ceil(hoursPerDay)} hours daily. <span style={{textDecoration: 'underline'}} onClick={() => rollbackSchedule(value, examDate)}>Undo Rebalance</span></>} />, {
      toastId: 'rebalance-complete',
      position: 'bottom-center',
      autoClose: false,
      hideProgressBar: true,
      draggableDirection: 'y',
      draggablePercent: 100,
      transition: Slide,
      closeButton: false,
      style: {
        padding: 0,
      }
    });
  };

  const openRollbackRebalanceConfirmation = async () => {
    const {data} = await API.graphql({query:`
      query getQuickCache($id: ID!) {
        getQuickCache(id: $id) {
          id
          content
        }
      }
    `, variables: {id: `${DEFAULT_USER_ID}-${bootcamp}-study-schedule-rollback`}, authMode: 'AMAZON_COGNITO_USER_POOLS'});
    const originalSchedule = JSON.parse(data.getQuickCache.content);
    const originalExamDate = searchUserInteractions(`testDate-rollback`, false);

    const originalIncompleteEvents = originalSchedule
      ? Object.values(originalSchedule).reduce((acc, events) => acc.concat(events.filter(event => !event.complete)), [])
      : [];
    const originalRemainingHours = Math.ceil(originalIncompleteEvents.reduce((acc, event) => acc + (parseInt(event.estimatedTime || 0) * (event?.custom ? 1 : studySpeed)), 0) / 60);
    const originalDaysUntilExam = originalExamDate
      ? differenceInDays(parse(originalExamDate, 'yyyy-MM-dd', new Date()), new Date()) + 1
      : 0;
    const originalOverdueIncompleteEvents = originalSchedule
      ? Object.entries(originalSchedule).reduce((acc, [date, events]) => {
        const today = new Date().toISOString().slice(0, 10);
        const isPast = date < today;
        if (isPast) {
          acc = acc.concat(events.filter(event => !event.complete));
        }
        return acc;
      }, [])
      : [];

    const originalHoursPerDay = Math.ceil(originalRemainingHours / originalDaysUntilExam);

    const currentDaysBetweenStartAndExam = examDate && Object.keys(value)[0]
      ? differenceInDays(parse(examDate, 'yyyy-MM-dd', new Date()), parse(Object.keys(value)[0], 'yyyy-MM-dd', new Date())) - (daysOff?.length || 0)
      : null;

    const currentHoursPerDay = currentDaysBetweenStartAndExam ? Math.ceil(remainingHours / currentDaysBetweenStartAndExam) : null;

    modalDispatch({
      type: 'open',
      component: UndoRebalanceConfirmation,
      enableClickClose: true,
      componentProps: {
        rollbackSchedule,
        remainingHours,
        currentSchedule: {
          startDate: Object.keys(value)[0],
          overdueIncompleteEvents,
          examDate,
          daysOff,
          hoursPerDay: currentHoursPerDay,
        },
        originalSchedule: {
          startDate: Object.keys(originalSchedule)[0],
          overdueIncompleteEvents: originalOverdueIncompleteEvents,
          examDate: originalExamDate,
          remainingHours: originalRemainingHours,
          hoursPerDay: originalHoursPerDay,
        },
        loading
      }
    });
  }

  const rollbackSchedule = async (value, examDate) => {
    setLoading(true);

    await API.graphql({query:`
      mutation updateQuickCache($input: UpdateQuickCacheInput!) {
        updateQuickCache(input: $input) {
          id
        }
      }
    `, variables: {input: {id: `${DEFAULT_USER_ID}-${bootcamp}-study-schedule`, content: JSON.stringify(value)}}, authMode: 'AMAZON_COGNITO_USER_POOLS'});
    setValue(value);

    await saveUserInteraction(`testDate`, JSON.stringify(examDate), true)

    setLoading(false);

    const incompleteEvents = value
      ? Object.values(value).reduce((acc, events) => acc.concat(events.filter(event => !event.complete)), [])
      : [];
    const remainingHours = Math.ceil(incompleteEvents.reduce((acc, event) => acc + (parseInt(event.estimatedTime || 0) * (event?.custom ? 1 : studySpeed)), 0) / 60);
    const totalEvents = value
      ? Object.values(value).reduce((acc, events) => acc + events.length, 0)
      : 0;
    const completedEvents = value
      ? Object.values(value).reduce((acc, events) => acc + events.filter(event => event.complete).length, 0)
      : 0;
    const daysUntilExam = examDate
      ? differenceInDays(parse(examDate, 'yyyy-MM-dd', new Date()), new Date()) + 1
      : 0;
    const overdueIncompleteEvents = value
      ? Object.entries(value).reduce((acc, [date, events]) => {
        const today = new Date().toISOString().slice(0, 10);
        const isPast = date < today;
        if (isPast) {
          acc = acc.concat(events.filter(event => !event.complete));
        }
        return acc;
      }, [])
      : [];

    const hoursPerDay = Math.ceil(remainingHours / daysUntilExam);

    toast(<UpdateComplete iconName="reply" title="Undo Rebalance Complete" body={overdueIncompleteEvents?.length == 0 ? `Studying about ${hoursPerDay} hour${hoursPerDay == 1 ? '' : 's'} daily.` : `Studying about ${hoursPerDay} hour${hoursPerDay == 1 ? '' : 's'} daily with ${overdueIncompleteEvents?.length <= 15 ? overdueIncompleteEvents?.length : '15+'} assignment${overdueIncompleteEvents?.length === 1 ? '' : 's'} overdue.`} />, {
      toastId: 'rebalance-rollback',
      position: 'bottom-center',
      autoClose: false,
      hideProgressBar: true,
      draggableDirection: 'y',
      draggablePercent: 100,
      transition: Slide,
      closeButton: false,
      style: {
        padding: 0,
      }
    });

    modalDispatch({type: 'close'});
  };

  const onCreate = async (startDate, daysOff, examDate, hoursPerDay) => {
    const distributedEvents = distributeEvents(content, startDate, daysOff, hoursPerDay, studySpeed);
    try {
      await API.graphql({query:`
        mutation createQuickCache($input: CreateQuickCacheInput!) {
          createQuickCache(input: $input) {
            id
          }
        }
      `, variables: {input: {id: `${DEFAULT_USER_ID}-${bootcamp}-study-schedule`, content: JSON.stringify(distributedEvents)}}, authMode: 'AMAZON_COGNITO_USER_POOLS'});
    } catch (error) {
      console.log(error);
      try {
        await API.graphql({query:`
          mutation updateQuickCache($input: UpdateQuickCacheInput!) {
            updateQuickCache(input: $input) {
              id
            }
          }
          `, variables: {input: {id: `${DEFAULT_USER_ID}-${bootcamp}-study-schedule`, content: JSON.stringify(distributedEvents)}}, authMode: 'AMAZON_COGNITO_USER_POOLS'});
      } catch (error) {
        console.log('failed', error);
      }
    }

    saveUserInteraction(`testDate`, JSON.stringify(examDate));
    setValue(distributedEvents);

    await IntercomAPI('trackEvent', 'Created Study Schedule', {startDate, examDate, daysOff: daysOff?.length, hoursPerDay: Math.ceil(hoursPerDay)});
  };
  const init = async () => {
    try {
      const {data} = await API.graphql({
        query: `
          query getQuickCache($id: ID!) {
            getQuickCache(id: $id) {
              id
              content
            }
          }
        `, variables: {id: `${DEFAULT_USER_ID}-${bootcamp}-study-schedule`}, authMode: 'AMAZON_COGNITO_USER_POOLS'
      });

      let integrityCheck = true;
      if (data.getQuickCache) {
        let cachedData = JSON.parse(data.getQuickCache?.content || '{}');
        // make sure there's at least one event in the schedule
        const hasEvents = Object.values(cachedData).some(events => events.length > 0);
        if (!hasEvents) {
          // save the schedule to a backup
          await API.graphql({query:`
            mutation createQuickCache($input: CreateQuickCacheInput!) {
              createQuickCache(input: $input) {
                id
              }
            }
          `, variables: {input: {id: `${DEFAULT_USER_ID}-${bootcamp}-study-schedule-delete`, content: JSON.stringify(cachedData)}}, authMode: 'AMAZON_COGNITO_USER_POOLS'});
          await API.graphql({query:`
            mutation deleteQuickCache($input: DeleteQuickCacheInput!) {
              deleteQuickCache(input: $input) {
                id
              }
            }
          `, variables: {input: {id: `${DEFAULT_USER_ID}-${bootcamp}-study-schedule`}}, authMode: 'AMAZON_COGNITO_USER_POOLS'});
          integrityCheck = false;
        }
      }

      if (data.getQuickCache && integrityCheck) {
        let cachedData = JSON.parse(data.getQuickCache?.content || '{}');
        // filter out any nulls from cached values
        cachedData = Object.entries(cachedData).reduce((acc, [date, events]) => {
          if (events) acc[date] = events.filter(event => !!event && !['Geometry Qbank'].includes(event.title)).map(event => event?.subject == 'custom' ? {...event, custom: true} : event);
          return acc;
        }, {});
        if (!Object.keys(cachedData).length) cachedData = null;
        setValue(cachedData);
      } else {
        const fetchResult = await Storage.get(`json/${bootcamp}/scheduleContent.json`, {download: true, cacheControl: 'no-cache'});
        let content = JSON.parse(await fetchResult.Body.text());
        if (!isBootcampPlusStudent) {
          // filter out content with Bootcamp Plus in the title
          content = content.filter(item => item.description ? !item.description.includes('Bootcamp Plus') : true);
        }
        if (['inbde', 'dat', 'med-school', 'oat'].includes(bootcamp)) {
          saveUserInteraction('useSubjectColors', 'true');
        }
        setContent(content);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!DEFAULT_USER_ID) return;

    init();
  }, [DEFAULT_USER_ID]);

  if (loading) return null; // add loader
  const totalHours = Math.ceil(content.reduce((acc, event) => acc + (parseInt(event?.estimatedTime || 0) * (event?.custom ? 1 : studySpeed)), 0) / 60);
  const hoursPerDay = value && Object.keys(value).length > 0 ? Object.entries(value).reduce((acc, [date, events]) => {
    const totalMinutes = events.reduce((acc, event) => acc + (parseInt(event.estimatedTime || 0) * (event?.custom ? 1 : studySpeed)), 0);
    return {...acc, [date]: totalMinutes / 60};
  }, {}) : {};
  const hasTenHourDays = Object.values(hoursPerDay).some(hours => hours >= 10);
  const averagingTenHourDays = (Object.values(hoursPerDay).reduce((acc, hours) => acc + hours, 0) / Object.keys(hoursPerDay).length) >= 10;
  return (
    <Container>
      <StyledBreadcrumbs history={history} lockAtDepth={2} />
      <PageTitle>Your Study Schedule</PageTitle>
      {overdueIncompleteEvents.length > 15 && <Blurb text={"You have more than 15 assignments overdue. Rebalance your schedule to get back on track!"} mobileText={"15+ assignments overdue"} />}
      {hasTenHourDays && !averagingTenHourDays && !['med-school'].includes(bootcamp) && <Blurb text={"You have 10+ hour study days, try rebalancing your schedule!"} mobileText={"10+ hours of study time"} />}
      {averagingTenHourDays && <Blurb text={"You're studying 10+ hours a day, try rebalancing or moving your exam date!"} mobileText={"10+ hours of study time"} />}
      {value
        ? <>
            <CalendarComponent
              eventsByDate={value}
              setEventsByDate={setValue}
              totalEvents={totalEvents}
              completedEvents={completedEvents}
              daysUntilExam={daysUntilExam}
              onClickRebalance={() => modalDispatch({
                type: 'open', component: RebalanceModal, enableClickClose: true, componentProps: {
                  rebalanceSchedule,
                  remainingHours,
                  rollbackSchedule,
                  currentSchedule: {
                    startDate: Object.keys(value)[0],
                    overdueIncompleteEvents,
                    examDate,
                    daysOff,
                }}})}
              onClickSettings={() => modalDispatch({type: 'open', component: SettingsModal, enableClickClose: true, componentProps: {resetSchedule, eventsByDate: value}})}
            />
          <StyledToastContainer />
        </>
        : <Create onCreate={onCreate} totalHours={totalHours}/>
      }
    </Container>
  );
};

const Wrapper = (props) => (
  <TooltipProvider>
    <StudySchedule {...props}/>
  </TooltipProvider>
);

export default Wrapper;
